import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PreferenceRow {
  readonly channel: string;
  readonly enabled: boolean;
}

export function NotificationsPreferencesPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PreferenceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [silentFrom, setSilentFrom] = useState<string>('');
  const [silentTo, setSilentTo] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: PreferenceRow[]; silentHoursFrom?: string; silentHoursTo?: string }>(
      '/notifications/preferences',
      { token },
    )
      .then((r) => {
        if (!alive) return;
        setRows(r.items);
        setSilentFrom(r.silentHoursFrom ?? '');
        setSilentTo(r.silentHoursTo ?? '');
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const toggle = async (channel: string, enabled: boolean): Promise<void> => {
    setBusy(channel);
    try {
      await apiRequest(`/notifications/preferences/${channel}`, { token, method: 'PUT', body: { enabled } });
      setRows((prev) => prev?.map((p) => (p.channel === channel ? { ...p, enabled } : p)) ?? prev);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="user-preferences">
      <h1>{t('notif.prefs.title')}</h1>
      <ul aria-label="channel-preferences">
        {rows.map((p) => (
          <li key={p.channel}>
            <label>
              <input
                type="checkbox"
                checked={p.enabled}
                disabled={busy === p.channel}
                onChange={(e) => void toggle(p.channel, e.target.checked)}
              />
              {p.channel}
            </label>
          </li>
        ))}
      </ul>
      <section aria-label="silent-hours">
        <h2>{t('notif.prefs.silentHours')}</h2>
        <label htmlFor="silent-from">{t('notif.prefs.from')}</label>
        <input id="silent-from" type="time" value={silentFrom} onChange={(e) => setSilentFrom(e.target.value)} />
        <label htmlFor="silent-to">{t('notif.prefs.to')}</label>
        <input id="silent-to" type="time" value={silentTo} onChange={(e) => setSilentTo(e.target.value)} />
      </section>
    </main>
  );
}
